import { useState, useRef } from 'react';
import styled from 'styled-components';

import erpApi from '/api/erp';
import LoadingCircle from '/components/ui/LoadingCircle';
import Description from '/components/ui/typography/Description';
import LegacyInput from '../../ui/LegacyInput';
import { colors } from '/css';
import { useOnClickOutside } from '/lib/helpers';

let timeout = null;

const MaterialFinder = ({
    onItemSelected,
    className = '',
    hidePrices = false,
    placeholder = '',
}) => {
    const [results, setResults] = useState([]);
    const [loading, setLoadingValue] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const inputWrapperRef = useRef();

    const onInputChange = async (val) => {
        if (!val) {
            setLoadingValue(false);
            clearInputAndResults();
            return;
        }

        setResults([]);

        setInputValue(val);
        setLoadingValue(true);

        clearTimeout(timeout);

        timeout = setTimeout(async () => {
            const results = await erpApi.findMaterials(val);

            if (results && results.data) {
                setResults(results.data.filter((r, i) => i < 5));
            } else {
                setResults([]);
            }
            setLoadingValue(false);
        }, 500);
    };

    const onResultClick = (item) => {
        onItemSelected({ ...item, quantity: 1 });
        clearInputAndResults();
    };

    const clearInputAndResults = () => {
        setInputValue('');
        setResults([]);
    };

    useOnClickOutside(inputWrapperRef, clearInputAndResults);
    return (
        <MaterialFinderWrapper className={className}>
            <div className="input-wrapper" ref={inputWrapperRef}>
                <LegacyInput
                    name="search-materials"
                    placeholder={placeholder || 'Søk'}
                    className="product-search-input"
                    value={inputValue}
                    onChange={onInputChange}
                    icon="search-glass"
                    clearable={inputValue && !loading}
                    onClear={clearInputAndResults}
                />
                {loading && <LoadingCircle color={colors.grey6} />}
            </div>
            <Description className="pt-8">
                Søk på artikkelnummer eller artikkelnavn
            </Description>
            {results.length > 0 && (
                <ul>
                    {results.map((res, i) => {
                        return (
                            <li
                                key={i}
                                onClick={() => onResultClick(res)}
                                className={
                                    'material-result material-result-' + i
                                }
                            >
                                <span className="img">
                                    {Array.isArray(res.picture) &&
                                    res.picture[0] ? (
                                        <img
                                            src={
                                                res.picture[0].url ||
                                                res.picture[0].uRL
                                            }
                                        />
                                    ) : null}
                                </span>
                                <span className="name">{res.name}</span>
                                <span
                                    className={`code ${
                                        hidePrices ? 'no-prices' : ''
                                    }`}
                                >
                                    {res.articleNo}
                                </span>
                                {!hidePrices && (
                                    <span className="price">
                                        {'Kr ' + res.price.price}
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </MaterialFinderWrapper>
    );
};

export default MaterialFinder;

const MaterialFinderWrapper = styled.div`
    position: relative;
    width: 100%;

    .input-wrapper {
        position: relative;
        z-index: 30;
        .search-icon,
        .clear-icon,
        .loading-circle {
            position: absolute;
            top: 50%;
            left: 24px;
            transform: translateY(-50%);
        }
        .loading-circle,
        .clear-icon {
            left: auto;
            right: 24px;
        }
        .clear-icon {
            cursor: pointer;
        }
    }

    ul {
        position: relative;
        margin-top: -58px;
        background: #fff;
        width: 100%;
        top: 0px;
        padding-top: 58px;
        border: 1px solid ${colors.grey3};
        border-radius: 12px;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
        overflow: hidden;
        right: 0;
        z-index: 29;
        li {
            border: 2px solid transparent;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            padding: 20px 24px;
            position: relative;
            &:after {
                content: '';
                width: calc(100% + 4px);
                height: 1px;
                display: block;
                position: absolute;
                top: -2px;
                right: -2px;
                background: ${colors.grey3};
            }
            &:first-child:after,
            &:hover + li:after {
                display: none;
            }
            &:hover {
                border: 2px solid ${colors.purple};
                &:after {
                    display: none;
                }
            }
            span {
                display: flex;
                align-items: center;
                &.img {
                    min-width: 60px;
                    max-width: 60px;
                    img {
                        display: block;
                    }
                }
                &.code {
                    min-width: 100px;
                    max-width: 100px;
                    font-family: 'gellix-semi';
                    &.no-prices {
                        justify-content: flex-end;
                    }
                }
                &.name {
                    flex: 1 1 auto;
                    padding: 0 18px;
                    font-family: 'gellix-semi';
                }
                &.price {
                    width: 80px;
                    min-width: 80px;
                    max-width: 80px;
                }
            }
            @media (max-width: 767px) {
                flex-wrap: wrap;
                > span {
                    width: 100% !important;
                    max-width: 100% !important;
                    padding: 0 !important;
                    margin-bottom: 8px;
                    flex-wrap: wrap;
                    &:before {
                        content: '-';
                        display: block;
                        width: 100%;
                        margin-bottom: 4px;
                        color: ${colors.grey6};
                    }
                    &.img {
                        max-width: 50px !important;
                        &:before {
                            display: none;
                        }
                    }
                    &.code:before {
                        content: 'Arikkelnr.';
                    }
                    &.name:before {
                        content: 'Arikkelnavn';
                    }
                    &.qty:before {
                        content: 'Kvantum';
                    }
                    &.price:before {
                        content: 'Pris';
                    }
                }
            }
        }
    }
`;
